/**
 * Authentication Store - Phase 1B
 * Real JWT-based authentication using backend APIs
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { webSocketService } from '@/services/websocket';
import { readJwtExpiryMs } from '@/utils/jwt';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'MANAGER' | 'DEVELOPER' | 'VIEWER';
  permissions: string[];
  avatar: string;
  lastLogin?: string;
}

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let bootstrapPromise: Promise<void> | null = null;

function clearProactiveAccessTokenRefresh(): void {
  if (proactiveRefreshTimer != null) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

function armProactiveAccessTokenRefresh(accessToken: string, refresh: () => Promise<void>): void {
  clearProactiveAccessTokenRefresh();
  const expMs = readJwtExpiryMs(accessToken);
  if (expMs == null) return;
  const msUntil = Math.max(expMs - Date.now() - 90_000, 10_000);
  proactiveRefreshTimer = setTimeout(async () => {
    proactiveRefreshTimer = null;
    try {
      await refresh();
    } catch {
      /* refreshAuth handles session expiry */
    }
  }, msUntil);
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;
  refreshToken: string | null;
  /** False until first session resolution (persist rehydrate + optional refresh) completes */
  sessionBootstrapped: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  /** Await on app shell load so API calls do not race ahead of access token restore */
  bootstrapSession: () => Promise<void>;
  initializeAuth: () => void;
  clearError: () => void;

  // Legacy methods for compatibility
  setUser: (user: User | null) => void;
  setToken: (token: string | null, refreshToken?: string | null) => void;
  clearAuth: () => void;
  hasPermission: (permission: string) => boolean;
}

const API_URL = import.meta.env.VITE_WS_URL || '';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      token: null,
      refreshToken: null,
      sessionBootstrapped: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_URL}/api/v1/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error?.message || 'Login failed');
          }

          const data = await response.json();

          localStorage.setItem('refresh_token', data.data.refreshToken);
          localStorage.setItem('user', JSON.stringify(data.data.user));

          armProactiveAccessTokenRefresh(data.data.accessToken, () => get().refreshAuth());

          set({
            user: data.data.user,
            isAuthenticated: true,
            token: data.data.accessToken,
            refreshToken: data.data.refreshToken,
            isLoading: false,
            error: null,
            sessionBootstrapped: true,
          });

          // After the token is stored, so the handshake reads it.
          try { webSocketService.reidentify(); } catch (_) { /* ignore */ }
        } catch (error) {
          clearProactiveAccessTokenRefresh();
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
          });
          throw error;
        }
      },

      logout: async () => {
        const { token } = get();

        clearProactiveAccessTokenRefresh();

        try {
          webSocketService.disconnect();
        } catch (_) {
          /* ignore */
        }

        try {
          if (token) {
            await fetch(`${API_URL}/api/v1/auth/logout`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
          }
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');

          set({
            user: null,
            isAuthenticated: false,
            token: null,
            refreshToken: null,
            error: null,
            isLoading: false,
            sessionBootstrapped: true,
          });
        }
      },

      refreshAuth: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          clearProactiveAccessTokenRefresh();
          set({ isAuthenticated: false, user: null, token: null, isLoading: false });
          return;
        }

        set({ isLoading: true });

        try {
          const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
          });

          if (!response.ok) {
            throw new Error('Token refresh failed');
          }

          const data = await response.json();

          if (data.data.refreshToken) {
            localStorage.setItem('refresh_token', data.data.refreshToken);
          }

          localStorage.setItem('user', JSON.stringify(data.data.user));

          armProactiveAccessTokenRefresh(data.data.accessToken, () => get().refreshAuth());

          set({
            token: data.data.accessToken,
            refreshToken: data.data.refreshToken || refreshToken,
            user: data.data.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // After the token is stored, so the handshake reads it.
          try { webSocketService.reidentify(); } catch (_) { /* ignore */ }
        } catch (error) {
          clearProactiveAccessTokenRefresh();
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');

          set({
            error: 'Session expired',
            isLoading: false,
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
          });
        }
      },

      bootstrapSession: async () => {
        if (get().sessionBootstrapped) return;
        if (!bootstrapPromise) {
          bootstrapPromise = (async () => {
            const lsRefresh =
              typeof localStorage !== 'undefined' ? localStorage.getItem('refresh_token') : null;

            set({ refreshToken: lsRefresh });

            if (lsRefresh) {
              await get().refreshAuth();
            } else {
              clearProactiveAccessTokenRefresh();
              set({
                isAuthenticated: false,
                token: null,
                refreshToken: null,
                user: null,
                isLoading: false,
              });
            }

            set({ sessionBootstrapped: true });
          })().finally(() => {
            bootstrapPromise = null;
          });
        }

        await bootstrapPromise;
      },

      initializeAuth: () => {
        void get().bootstrapSession();
      },

      clearError: () => {
        set({ error: null });
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user });
      },

      setToken: (token: string | null, refreshToken?: string | null) => {
        set({ token, refreshToken: refreshToken || null });
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken);
        } else {
          localStorage.removeItem('refresh_token');
        }
        if (token) armProactiveAccessTokenRefresh(token, () => get().refreshAuth());
        else clearProactiveAccessTokenRefresh();
      },

      clearAuth: () => {
        clearProactiveAccessTokenRefresh();
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        set({
          user: null,
          isAuthenticated: false,
          token: null,
          refreshToken: null,
          error: null,
          isLoading: false,
          sessionBootstrapped: true,
        });
      },

      hasPermission: (permission: string) => {
        const { user } = get();
        if (!user) return false;
        return user.permissions.includes('*') || user.permissions.includes(permission);
      },
    }),
    {
      name: 'auth-storage',
      version: 2,
      migrate: (persisted) => {
        const p = persisted as Record<string, unknown> | null;
        if (p && typeof p === 'object') {
          delete p.isAuthenticated;
          delete p.token;
          delete p.refreshToken;
        }
        return persisted;
      },
      partialize: (state) => ({
        user: state.user,
      }),
    },
  ),
);

if (typeof window !== 'undefined') {
  (window as any).__authStore = useAuthStore;
}

export default useAuthStore;
